<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId } from "vue";
import PerkBadge from "@/Components/PerkBadge.vue";

/**
 * Ряд значков с подсказкой.
 *
 * На компьютере подсказка открывается наведением, на телефоне — касанием:
 * hover там не существует, и без клика значок остался бы просто картинкой.
 *
 * Текст подсказки ВСЕГДА лежит в разметке и прячется только видимостью. Если
 * прятать его через v-if, объяснение исчезнет из HTML — а это единственное
 * место, где сказано, чем значок заслужен, и его должны видеть и поисковик, и
 * программа чтения с экрана.
 */
const props = defineProps({
  perks: { type: Array, default: () => [] },
  size: { type: Number, default: 64 },
  withName: { type: Boolean, default: false },   // подпись рядом со значком
  muted: { type: Boolean, default: false },      // серый до наведения — для карточек
});

/**
 * Свой префикс id на каждый ряд. На странице каталога карточек десять, и ключи
 * значков в них повторяются — id вида «perk-tip-orator» встретился бы десять раз.
 * Дубли делают разметку невалидной, а aria-describedby указывает на первый
 * попавшийся элемент, и озвучка читает подсказку чужой карточки.
 *
 * useId, а не случайное число: он даёт одинаковый результат на сервере и в
 * браузере, иначе развалилась бы гидратация.
 */
const uid = useId();
const tipId = (p) => `${uid}-${p.key}`;

const open = ref(-1);
const root = ref(null);

/**
 * Подсказку держим в пределах экрана.
 *
 * ⚠ ЧТО БЫЛО НЕ ТАК. Раньше выравнивание считалось по номеру значка: первый —
 * к левому краю, последний — к правому, остальные по центру. На широком экране
 * это работало, а на телефоне у карточки ширина 358 px, подсказка 256 px, и
 * центрированная под третьим из пяти значков она наполовину уезжала за экран.
 * Номер значка ничего не знает о том, где значок оказался физически.
 *
 * Поэтому считаем при открытии: хотим центр подсказки под центром значка, но
 * зажимаем результат в границы окна с полем в 8 px. Пересчитываем при
 * повороте экрана — там ширина меняется, а подсказка остаётся открытой.
 */
const tipShift = ref(0);

async function place(i) {
  if (i < 0 || typeof window === "undefined") return;
  await nextTick();
  const box = root.value?.children?.[i];
  const tip = box?.querySelector("[data-tip]");
  if (!box || !tip) return;

  const b = box.getBoundingClientRect();
  const w = tip.offsetWidth;
  const vw = document.documentElement.clientWidth;
  const M = 8;

  const wanted = b.left + b.width / 2 - w / 2;
  const clamped = Math.max(M, Math.min(wanted, vw - w - M));
  tipShift.value = clamped - b.left;
}

const onResize = () => { if (open.value >= 0) place(open.value); };

// Касание мимо ряда закрывает подсказку — иначе на телефоне она висит до перезагрузки.
const onDocClick = (e) => {
  if (root.value && !root.value.contains(e.target)) open.value = -1;
};
const onEsc = (e) => { if (e.key === "Escape") open.value = -1; };

onMounted(() => {
  document.addEventListener("click", onDocClick);
  window.addEventListener("resize", onResize);
  document.addEventListener("keydown", onEsc);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  document.removeEventListener("keydown", onEsc);
  window.removeEventListener("resize", onResize);
});

// Наведение только там, где мышь действительно есть: на тач-экранах hover
// «залипает» после касания и подсказка не закрывается.
const hoverable = computed(() => typeof window !== "undefined"
  && window.matchMedia?.("(hover: hover)").matches);

const show = (i) => { if (hoverable.value) { open.value = i; place(i); } };
const hide = () => { if (hoverable.value) open.value = -1; };
const toggle = (i) => {
  open.value = open.value === i ? -1 : i;
  if (open.value >= 0) place(open.value);
};
</script>

<template>
  <div ref="root" class="flex flex-wrap" :class="muted ? 'gap-x-3 gap-y-1.5' : 'gap-3 sm:gap-4'">
    <div v-for="(p, i) in perks" :key="p.key" class="relative">
      <button
        type="button"
        class="group/perk flex items-center gap-1.5 transition-transform hover:scale-[1.03] focus:outline-none
               focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        :class="[
          withName ? 'rounded-lg text-xs text-gray-600' : 'rounded-full',
          open === i ? (withName ? 'text-indigo-700' : 'ring-2 ring-indigo-500 ring-offset-2') : '',
        ]"
        :aria-expanded="open === i"
        :aria-describedby="tipId(p)"
        @mouseenter="show(i)"
        @mouseleave="hide()"
        @focus="show(i)"
        @blur="hide()"
        @click.stop="toggle(i)"
      >
        <PerkBadge :perk="p" :size="size" :muted="muted" />
        <!-- Название видимым текстом, когда просят: подпись читают и человек, и
             поисковик. Без неё имя остаётся только для программы чтения. -->
        <span :class="withName ? '' : 'sr-only'">{{ p.name }}</span>
      </button>

      <div
        :id="tipId(p)"
        role="tooltip"
        data-tip
        class="absolute bottom-full left-0 z-30 mb-3 w-64 max-w-[calc(100vw-1rem)] rounded-xl bg-gray-900 px-3.5 py-3
               text-left text-white shadow-xl transition-opacity duration-150"
        :style="open === i ? { transform: 'translateX(' + tipShift + 'px)' } : null"
        :class="open === i ? 'opacity-100' : 'pointer-events-none opacity-0'"
      >
        <p class="text-sm font-semibold leading-snug">
          {{ p.name }}<span v-if="p.short" class="font-normal text-gray-300"> · {{ p.short }}</span>
        </p>
        <!-- Личная история выдачи, если она записана: общий текст значка одинаков
             у всех, кому он выдан, и о конкретном человеке ничего не говорит. -->
        <p class="mt-1 text-xs leading-relaxed text-gray-300">{{ p.story || p.check }}</p>
      </div>
    </div>
  </div>
</template>
